import React, {
  useRef,
  useState,
  ReactElement,
  ReactNode,
  useEffect,
  cloneElement,
  forwardRef,
} from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  Placement,
  useMergeRefs,
} from "@floating-ui/react";
import { useCss, k, a } from "kremling";

interface CpTooltipProps {
  children: ReactElement | (() => ReactElement);
  delay?: number;
  disabled?: boolean;
  interactive?: boolean;
  position?:
    | Placement
    | "bottom-right"
    | "bottom-left"
    | "top-right"
    | "top-left"
    | "left-top"
    | "left-bottom"
    | "right-top"
    | "right-bottom";
  text: string | ReactNode;
}

const FADE_ANIM_DURATION = 200; // Time for fade in/out in milliseconds

export const CpTooltip = forwardRef<any, CpTooltipProps>(function CpTooltip(
  {
    children,
    delay = 200,
    disabled,
    interactive = false,
    position = "bottom",
    text,
  },
  ref,
) {
  const scope = useCss(css);
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [mounted, setMounted] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);
  const placement = placementConversion(position);

  const {
    refs,
    floatingStyles,
    context,
    middlewareData,
    placement: resolvedPlacement,
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(10), flip(), shift(), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    move: false,
    delay: {
      open: delay,
      close: interactive ? 500 : 0,
    },
    handleClose: interactive ? undefined : null,
  });

  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const timer = setTimeout(() => setShow(true));
      return () => clearTimeout(timer);
    } else if (mounted) {
      setShow(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setMounted(false), FADE_ANIM_DURATION);
      return () => clearTimeout(timer);
    }
  }, [open, mounted]);

  const staticSide = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right",
  }[resolvedPlacement.split("-")[0]];

  const arrowStyles = {
    left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : "",
    top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : "",
    ...(staticSide ? { [staticSide]: "-0.4rem" } : {}),
  };

  const renderedChildren =
    typeof children === "function" ? children() : children;
  const childIsFunction = typeof renderedChildren.type === "function";

  // In some cases (like dropdown triggers) the children might have their own ref so we need to merge them with what useFloating provides.
  const mergedRef = useMergeRefs([
    refs.setReference,
    ref,
    (renderedChildren as any).ref,
  ]);

  return (
    <>
      {childIsFunction ? (
        <div style={{ display: "inline-block" }} ref={mergedRef}>
          {renderedChildren}
        </div>
      ) : (
        cloneElement(renderedChildren, {
          ref: mergedRef,
        })
      )}

      {mounted && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 100001 }}
            {...getFloatingProps()}
            {...scope}
            className={a("cp-tooltip").m("cp-tooltip--show", show)}
            aria-expanded={open}
          >
            {text}
            <div
              className="cp-tooltip-arrow"
              ref={arrowRef}
              style={arrowStyles}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
});

const css = k`
  .cp-tooltip {
    font-size: 1.2rem;
    background: var(--cp-color-tooltip-bg);
    color: var(--cp-color-tooltip-text);
    padding: .5rem 1rem;
    border-radius: var(--cp-form-border-radius);
    font-weight: 500;
    max-width: 28rem;
    opacity: 0;
    transition: opacity ${FADE_ANIM_DURATION.toString()}ms ease;
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: auto;
  }
  .cp-tooltip--show {
    opacity: 1;
  }
  @media screen and (max-width: 599px) {
    .cp-tooltip--show{
      opacity: 0;
    }
  }

  .cp-tooltip-arrow,
  .cp-tooltip-arrow::before {
    position: absolute;
    width: .8rem;
    height: .8rem;
    background: inherit;
  }

  .cp-tooltip-arrow {
    visibility: hidden;
  }

  .cp-tooltip-arrow::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
  }

  .cp-tooltip[data-placement^='top'] > .cp-tooltip-arrow {
    bottom: -.4rem;
  }

  .cp-tooltip[data-placement^='bottom'] > .cp-tooltip-arrow {
    top: -.4rem;
  }

  .cp-tooltip[data-placement^='left'] > .cp-tooltip-arrow {
    right: -.4rem;
  }

  .cp-tooltip[data-placement^='right'] > .cp-tooltip-arrow {
    left: -.4rem;
  }
`;

function placementConversion(position: string): Placement {
  let placement: Placement;
  switch (position) {
    case "top-left":
      placement = "top-start";
      break;
    case "top-right":
      placement = "top-end";
      break;
    case "left-top":
      placement = "left-start";
      break;
    case "left-bottom":
      placement = "left-end";
      break;
    case "right-top":
      placement = "right-start";
      break;
    case "right-bottom":
      placement = "right-end";
      break;
    case "bottom-left":
      placement = "bottom-start";
      break;
    case "bottom-right":
      placement = "bottom-end";
      break;
    default:
      placement = position as Placement;
  }
  return placement;
}
